import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";
import { InvitationPreviewDTO, PagedDTO, WorkshopBody, WorkshopDetailDTO, WorkshopRowDTO } from "../../models/WorkshopDTO";

const base = (campusId: number) => `/admin/campus/${campusId}/workshops`;

/** Multi-día: una sesión del taller (GET _details → sessions[]). Fecha "YYYY-MM-DD", horas "HH:MM". */
export interface WorkshopSessionDTO {
	id: number;
	date: string;
	startTime: string;
	endTime: string | null;
	classroomId: number | null;
	classroomName: string | null;
	mentorId: number | null;
	mentorName: string | null;
	notes: string | null;
	attendance: { attended: number; total: number };
}

/** Lista: + sessionsCount (0 = taller de un solo día). */
export type WorkshopRowMultiDTO = WorkshopRowDTO & { sessionsCount?: number };

export interface WorkshopSessionBody {
	date?: string;
	startTime?: string;
	endTime?: string | null;
	classroomId?: number | null;
	mentorId?: number | null;
	notes?: string | null;
}

/** El detalle con sesiones: cada inscrita trae sessions[{sessionId, attended}]. */
export type WorkshopMultiDetailDTO = Omit<WorkshopDetailDTO, "attendees"> & {
	sessions?: WorkshopSessionDTO[];
	sessionsCount?: number;
	attendees: (WorkshopDetailDTO["attendees"][number] & { sessions?: { sessionId: number; attended: boolean }[] })[];
};

/** Talleres (FASE 4). */
export class WorkshopsService {
	list = async (campusId: number, f: { status?: string; q?: string; from?: string; to?: string; page?: number; perPage?: number } = {}): Promise<AxiosResponse<PagedDTO<WorkshopRowDTO>>> => {
		const params: { [k: string]: string | number } = {};
		Object.entries(f).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") params[k] = v as any; });
		return axiosInstance.get<PagedDTO<WorkshopRowDTO>>(base(campusId), { params });
	}

	details = async (campusId: number, id: number): Promise<AxiosResponse<WorkshopDetailDTO>> =>
		axiosInstance.get<WorkshopDetailDTO>(`${base(campusId)}/_details`, { params: { id } });

	create = async (campusId: number, body: WorkshopBody): Promise<AxiosResponse<WorkshopDetailDTO>> =>
		axiosInstance.post<WorkshopDetailDTO>(base(campusId), body);

	/** Anular anula los cobros sin abonos; con abonos 422. */
	update = async (campusId: number, body: WorkshopBody & { id: number }): Promise<AxiosResponse<WorkshopDetailDTO>> =>
		axiosInstance.put<WorkshopDetailDTO>(base(campusId), body);

	/** 422: lleno, repetida, alumna de otro plantel. */
	addAttendee = async (campusId: number, body: { workshopId: number; studentId: number; charge?: boolean; price?: number; note?: string }): Promise<AxiosResponse<WorkshopDetailDTO>> =>
		axiosInstance.post<WorkshopDetailDTO>(`${base(campusId)}/_attendees`, body);

	setAttendance = async (campusId: number, attendeeId: number, attended: boolean): Promise<AxiosResponse<any>> =>
		axiosInstance.put(`${base(campusId)}/_attendance`, { attendeeId, attended });

	/** Con abonos 422. */
	removeAttendee = async (campusId: number, attendeeId: number, note?: string): Promise<AxiosResponse<WorkshopDetailDTO>> =>
		axiosInstance.put<WorkshopDetailDTO>(`${base(campusId)}/_removeAttendee`, { attendeeId, note });

	/** Multi-día: alta de sesión. 422: salida ≤ entrada, aula de otro plantel, taller anulado/histórico. */
	addSession = async (campusId: number, workshopId: number, body: WorkshopSessionBody): Promise<AxiosResponse<WorkshopMultiDetailDTO>> =>
		axiosInstance.post<WorkshopMultiDetailDTO>(`${base(campusId)}/_sessions`, { ...body, workshopId });

	updateSession = async (campusId: number, id: number, body: WorkshopSessionBody): Promise<AxiosResponse<WorkshopMultiDetailDTO>> =>
		axiosInstance.put<WorkshopMultiDetailDTO>(`${base(campusId)}/_sessions`, { ...body, id });

	/** Borra también la asistencia de esa sesión. */
	deleteSession = async (campusId: number, id: number): Promise<AxiosResponse<WorkshopMultiDetailDTO>> =>
		axiosInstance.delete<WorkshopMultiDetailDTO>(`${base(campusId)}/_sessions`, { params: { id } });

	/** 422: alumna de baja o de otro taller, taller anulado. */
	setSessionAttendance = async (campusId: number, sessionId: number, attendeeId: number, attended: boolean): Promise<AxiosResponse<WorkshopMultiDetailDTO>> =>
		axiosInstance.put<WorkshopMultiDetailDTO>(`${base(campusId)}/_sessionAttendance`, { sessionId, attendeeId, attended });

	invitationPreview = async (campusId: number, workshopId: number, courseIds: number[], text?: string): Promise<AxiosResponse<InvitationPreviewDTO>> =>
		axiosInstance.get<InvitationPreviewDTO>(`${base(campusId)}/_invitation`, { params: { workshopId, courseIds: courseIds.join(","), text: text || undefined } });
}

const workshopsService = new WorkshopsService();
export default workshopsService;
